import clsx from 'clsx';
import twClasses from '@/utils/twClasses';
import { Field, useField, useFormikContext } from 'formik';
import { ErrorDisplay } from '@/components/formComponents';
import { useEffect, useRef } from 'react';

interface TextInputProps {
    name: string;
    label?: string;
    className?: string;
    inputClassName?: string;
    type?: string;
    container?: 'input' | 'textarea';
    placeholder?: string;
    showError?: boolean;
    onChange?: (
        _e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
}

const TextInput = ({
    name,
    label,
    className = '',
    inputClassName = '',
    type = 'text',
    container = 'input',
    placeholder = '',
    showError = true,
    onChange,
}: TextInputProps) => {
    // SCSC The meta object contains information about the state of the form element
    // so we can use this for error reporting
    const [field, meta] = useField(name);
    const { errors, isSubmitting, values, handleChange, handleBlur } =
        useFormikContext<Record<string, unknown>>();

    const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    useEffect(() => {
        const errorKeys = Object.keys(errors);
        const firstError = errorKeys[0];

        if (isSubmitting && firstError === name) {
            fieldRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [isSubmitting, name, errors]);

    const isInErrorState = meta.touched && meta.error;

    const inputClasses = clsx(
        'form-control',
        // transparent border to stop page shift
        isInErrorState ? twClasses.inputError : 'border-2 border-transparent',
        'p-2',
        'w-full',
        'focus:ring-gray-200',
        inputClassName
    );

    const containerClasses = clsx(
        'form-group',
        'flex',
        'w-full',
        'flex-wrap',
        className,
        isInErrorState && 'field-error'
    );

    const handleValueChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (onChange) onChange(e);
        handleChange(e);
    };

    return (
        <div className={containerClasses}>
            {label && (
                <label className="w-full" htmlFor={name}>
                    {label}
                </label>
            )}

            <Field
                {...field}
                innerRef={fieldRef}
                as={container}
                type={container === 'input' ? type : undefined}
                id={name}
                className={inputClasses}
                placeholder={placeholder}
                value={(values[name] as string | number) ?? ''}
                onChange={handleValueChange}
                onBlur={handleBlur}
            />

            {showError && <ErrorDisplay meta={meta} />}
        </div>
    );
};

export default TextInput;
